import { useEffect, useState } from "react";
import api, { fmtErr } from "@/lib/api";

const SENTIMENTS = ["confident", "patient", "rushed", "fomo", "revenge", "neutral"];

export default function JournalModal({ onClose, tier }) {
  const [tab, setTab] = useState("entries");
  const [entries, setEntries] = useState([]);
  const [form, setForm] = useState({ title: "", notes: "", setup: "", sentiment: "neutral", tags: "" });
  const [analytics, setAnalytics] = useState(null);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [err, setErr] = useState("");

  const isPro = tier === "pro" || tier === "elite";

  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({ title: "", notes: "", setup: "", sentiment: "", tags: "" });

  useEffect(() => { reload(); }, []);
  async function reload() {
    try { const { data } = await api.get("/journal/entries"); setEntries(data); }
    catch (e) { setErr(fmtErr(e.response?.data?.detail) || e.message); }
  }
  function startEdit(en) {
    setEditingId(en.id);
    setEditDraft({
      title: en.title || "", notes: en.notes || "",
      setup: en.setup || "", sentiment: en.sentiment || "neutral",
      tags: (en.tags || []).join(", "),
    });
  }
  async function saveEdit(id) {
    try {
      const payload = {
        title: editDraft.title, notes: editDraft.notes,
        setup: editDraft.setup, sentiment: editDraft.sentiment,
        tags: editDraft.tags ? editDraft.tags.split(",").map(s => s.trim()).filter(Boolean) : [],
      };
      await api.patch(`/journal/entries/${id}`, payload);
      setEditingId(null); await reload();
    } catch (e2) { setErr(fmtErr(e2.response?.data?.detail) || e2.message); }
  }
  async function submit(e) {
    e.preventDefault();
    setErr("");
    try {
      const payload = { ...form, tags: form.tags ? form.tags.split(",").map(s => s.trim()).filter(Boolean) : [] };
      await api.post("/journal/entries", payload);
      setForm({ title: "", notes: "", setup: "", sentiment: "neutral", tags: "" });
      await reload();
    } catch (e2) { setErr(fmtErr(e2.response?.data?.detail) || e2.message); }
  }
  async function remove(id) {
    if (!window.confirm("Delete this journal entry?")) return;
    try { await api.delete(`/journal/entries/${id}`); await reload(); }
    catch (e) { setErr(fmtErr(e.response?.data?.detail) || e.message); }
  }
  async function runAnalytics() {
    if (!isPro) { setErr("Pattern analytics requires Pro tier."); return; }
    setErr(""); setLoadingAnalytics(true); setAnalytics(null);
    try {
      const { data } = await api.get("/journal/analytics");
      setAnalytics(data);
    } catch (e) { setErr(fmtErr(e.response?.data?.detail) || e.message); }
    finally { setLoadingAnalytics(false); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 font-mono" data-testid="journal-modal">
      <div className="bg-[#0f131b] border border-slate-800 rounded-2xl w-full max-w-5xl h-[88vh] flex flex-col overflow-hidden">
        <header className="px-5 py-3 border-b border-slate-800 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-indigo-600/20 text-indigo-300 rounded-lg border border-indigo-500/30">
              <i className="fa-solid fa-book"/>
            </div>
            <div>
              <div className="text-white font-bold text-sm">Trade Journal</div>
              <div className="text-[10px] text-slate-500">Log setups, sentiment, and unlock AI pattern analytics</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="journal-tab-entries" onClick={() => setTab("entries")}
              className={`text-xs px-3 py-1.5 rounded font-bold ${tab === "entries" ? "bg-indigo-600/30 text-indigo-200" : "text-slate-400 hover:text-slate-200"}`}>
              <i className="fa-solid fa-pen mr-1"/> Entries
            </button>
            <button data-testid="journal-tab-analytics" onClick={() => setTab("analytics")}
              className={`text-xs px-3 py-1.5 rounded font-bold ${tab === "analytics" ? "bg-indigo-600/30 text-indigo-200" : "text-slate-400 hover:text-slate-200"}`}>
              <i className="fa-solid fa-chart-pie mr-1"/> AI Analytics {!isPro && <span className="ml-1">🔒</span>}
            </button>
            <button data-testid="journal-close" onClick={onClose} className="text-slate-500 hover:text-rose-400 text-xl ml-2">
              <i className="fa-solid fa-xmark"/>
            </button>
          </div>
        </header>

        {err && <div data-testid="journal-err" className="m-4 text-xs text-rose-300 bg-rose-500/10 border border-rose-500/20 px-3 py-2 rounded">{err}</div>}

        {tab === "entries" ? (
          <div className="flex-1 grid grid-cols-[380px_1fr] overflow-hidden">
            <form onSubmit={submit} className="border-r border-slate-800 p-4 space-y-3 overflow-y-auto" data-testid="journal-form">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">New Entry</div>
              <input data-testid="je-title" required placeholder="Title (e.g., London BSL sweep)" value={form.title}
                onChange={e => setForm({ ...form, title: e.target.value })}
                className="w-full px-3 py-2 bg-[#161d28] border border-slate-700 rounded-lg text-sm focus:outline-none focus:border-indigo-500"/>
              <textarea data-testid="je-notes" placeholder="What went right, what went wrong?" value={form.notes}
                onChange={e => setForm({ ...form, notes: e.target.value })} rows={4}
                className="w-full px-3 py-2 bg-[#161d28] border border-slate-700 rounded-lg text-sm focus:outline-none focus:border-indigo-500 resize-none"/>
              <input data-testid="je-setup" placeholder="Setup (e.g., FVG_fill, BSL_sweep)" value={form.setup}
                onChange={e => setForm({ ...form, setup: e.target.value })}
                className="w-full px-3 py-2 bg-[#161d28] border border-slate-700 rounded-lg text-sm focus:outline-none focus:border-indigo-500"/>
              <select data-testid="je-sentiment" value={form.sentiment} onChange={e => setForm({ ...form, sentiment: e.target.value })}
                className="w-full px-3 py-2 bg-[#161d28] border border-slate-700 rounded-lg text-sm focus:outline-none cursor-pointer">
                {SENTIMENTS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <input data-testid="je-tags" placeholder="Tags (comma-separated)" value={form.tags}
                onChange={e => setForm({ ...form, tags: e.target.value })}
                className="w-full px-3 py-2 bg-[#161d28] border border-slate-700 rounded-lg text-sm focus:outline-none focus:border-indigo-500"/>
              <button data-testid="je-save" type="submit" className="w-full py-2 bg-indigo-600 hover:bg-indigo-700 rounded-lg text-xs font-bold text-white">
                <i className="fa-solid fa-plus mr-1"/> Save Entry
              </button>
            </form>
            <div className="overflow-y-auto p-4 space-y-2" data-testid="journal-entries">
              {entries.length === 0 && <div className="text-slate-500 text-sm text-center mt-8">No journal entries yet. Log your first trade thesis on the left.</div>}
              {entries.map(en => (
                <div key={en.id} data-testid={`journal-entry-${en.id}`} className="bg-slate-900/60 border border-slate-800 rounded-lg p-3">
                  {editingId === en.id ? (
                    <div className="space-y-2">
                      <input data-testid="je-edit-title" value={editDraft.title} onChange={e => setEditDraft({ ...editDraft, title: e.target.value })}
                        className="w-full px-2 py-1.5 bg-[#161d28] border border-slate-700 rounded text-xs font-bold"/>
                      <textarea data-testid="je-edit-notes" rows={3} value={editDraft.notes} onChange={e => setEditDraft({ ...editDraft, notes: e.target.value })}
                        className="w-full px-2 py-1.5 bg-[#161d28] border border-slate-700 rounded text-xs resize-none"/>
                      <div className="flex gap-2">
                        <input data-testid="je-edit-setup" placeholder="Setup" value={editDraft.setup} onChange={e => setEditDraft({ ...editDraft, setup: e.target.value })}
                          className="flex-1 px-2 py-1.5 bg-[#161d28] border border-slate-700 rounded text-xs"/>
                        <select data-testid="je-edit-sentiment" value={editDraft.sentiment} onChange={e => setEditDraft({ ...editDraft, sentiment: e.target.value })}
                          className="flex-1 px-2 py-1.5 bg-[#161d28] border border-slate-700 rounded text-xs cursor-pointer">
                          {SENTIMENTS.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                      <input data-testid="je-edit-tags" placeholder="tags" value={editDraft.tags} onChange={e => setEditDraft({ ...editDraft, tags: e.target.value })}
                        className="w-full px-2 py-1.5 bg-[#161d28] border border-slate-700 rounded text-xs"/>
                      <div className="flex gap-2">
                        <button data-testid="je-edit-save" onClick={() => saveEdit(en.id)} className="flex-1 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-xs font-bold">Save</button>
                        <button data-testid="je-edit-cancel" onClick={() => setEditingId(null)} className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded text-xs font-bold">Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex justify-between items-start">
                        <div className="font-bold text-sm text-white">{en.title}</div>
                        <div className="flex gap-1">
                          <button data-testid={`je-edit-${en.id}`} onClick={() => startEdit(en)} className="text-slate-600 hover:text-blue-300 text-xs" title="Edit"><i className="fa-solid fa-pen"/></button>
                          <button onClick={() => remove(en.id)} className="text-slate-600 hover:text-rose-400 text-xs" title="Delete"><i className="fa-solid fa-trash"/></button>
                        </div>
                      </div>
                      <div className="text-[10px] text-slate-500 mt-1">
                        {en.created_at?.slice(0,16).replace("T"," ")} ·
                        {en.setup ? <span className="text-cyan-400 ml-1">{en.setup}</span> : null}
                        {en.sentiment ? <span className="text-amber-400 ml-2">· {en.sentiment}</span> : null}
                        {en.edited_at ? <span className="text-slate-600 ml-2 italic">(edited)</span> : null}
                      </div>
                      {en.notes && <div className="text-xs text-slate-300 mt-2 whitespace-pre-wrap">{en.notes}</div>}
                      {en.tags?.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {en.tags.map(t => <span key={t} className="text-[9px] px-1.5 py-0.5 bg-slate-800 text-slate-400 rounded">#{t}</span>)}
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto p-6">
            {!isPro ? (
              <div className="text-center py-16">
                <i className="fa-solid fa-lock text-5xl text-amber-300 mb-3"/>
                <div className="text-white font-bold mb-2">AI Pattern Analytics is a Pro feature</div>
                <div className="text-slate-400 text-sm">Upgrade to unlock Claude-powered win/loss pattern detection and weekly recaps.</div>
                <a href="/account?tab=plan" className="inline-block mt-4 px-5 py-2 bg-gradient-to-r from-amber-500 to-orange-500 text-slate-900 rounded-lg text-xs font-black">
                  <i className="fa-solid fa-crown mr-1"/> Upgrade
                </a>
              </div>
            ) : (
              <div className="space-y-5 max-w-3xl">
                <button data-testid="run-analytics" onClick={runAnalytics} disabled={loadingAnalytics}
                  className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 rounded-lg text-xs font-bold text-white">
                  {loadingAnalytics ? <><i className="fa-solid fa-spinner fa-spin mr-1"/> Analyzing 30-day trades…</> : <><i className="fa-solid fa-brain mr-1"/> Run Pattern Analytics</>}
                </button>
                {analytics && (
                  <div className="space-y-4" data-testid="analytics-result">
                    <div className="grid grid-cols-3 gap-3">
                      <Stat label="Trades (30d)" value={analytics.sample_size}/>
                      <Stat label="Win Rate" value={`${analytics.win_rate}%`}/>
                      <Stat label="Net PnL" value={`$${analytics.net_pnl}`} color={analytics.net_pnl >= 0 ? "text-emerald-300" : "text-rose-300"}/>
                    </div>
                    <Block title="Weekly Recap" icon="fa-feather" color="text-indigo-300">
                      <p className="text-slate-300 text-sm leading-relaxed">{analytics.weekly_recap}</p>
                    </Block>
                    <div className="grid md:grid-cols-2 gap-4">
                      <Block title="Winning Patterns" icon="fa-trophy" color="text-emerald-300">
                        <ul className="space-y-1.5">{analytics.winning_patterns.map((p, i) => <li key={i} className="text-xs text-slate-300"><i className="fa-solid fa-check text-emerald-400 mr-1.5"/>{p}</li>)}</ul>
                      </Block>
                      <Block title="Losing Patterns" icon="fa-triangle-exclamation" color="text-rose-300">
                        <ul className="space-y-1.5">{analytics.losing_patterns.map((p, i) => <li key={i} className="text-xs text-slate-300"><i className="fa-solid fa-xmark text-rose-400 mr-1.5"/>{p}</li>)}</ul>
                      </Block>
                    </div>
                    <Block title="Discipline Score" icon="fa-gauge" color="text-amber-300">
                      <div className="flex items-center gap-3">
                        <div className="text-4xl font-black text-white">{analytics.discipline_score}<span className="text-base text-slate-500">/100</span></div>
                        <div className="flex-1 h-3 bg-slate-800 rounded-full overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-rose-500 via-amber-400 to-emerald-500" style={{ width: `${analytics.discipline_score}%` }}/>
                        </div>
                      </div>
                    </Block>
                    <Block title="This Week's Top Action" icon="fa-bullseye" color="text-cyan-300">
                      <p className="text-slate-200 text-sm leading-relaxed">{analytics.top_recommendation}</p>
                    </Block>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, color = "text-white" }) {
  return (
    <div className="bg-slate-900/60 border border-slate-800 rounded-lg p-3">
      <div className="text-[10px] text-slate-500 uppercase">{label}</div>
      <div className={`text-lg font-bold ${color}`}>{value}</div>
    </div>
  );
}
function Block({ title, icon, color, children }) {
  return (
    <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4">
      <div className={`text-[10px] uppercase tracking-widest font-bold mb-2 ${color}`}><i className={`fa-solid ${icon} mr-1.5`}/>{title}</div>
      {children}
    </div>
  );
}
